import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import {
  DashboardOutlined,
  BookOutlined,
  FolderOpenOutlined,
  PictureOutlined,
  ShareAltOutlined,
  InboxOutlined,
  QuestionCircleOutlined,
  CalendarOutlined,
  FileTextOutlined,
  LoginOutlined,
  LogoutOutlined,
  CloseOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  FormOutlined,
} from '@ant-design/icons';
import { useServices } from 'src/services';
import { useStores } from 'src/stores';
import moment from 'moment';
import fordLogo from '../assets/images/Ford_BuildingTogether_Blue_Screen_RGB_Vt_v2.png';
import './SidebarNavContainer.scss';

interface NavLink {
  path: string;
  title: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  hidden?: boolean;
  onClick?: () => void;
}

const SidebarNavContainer = (props: any) => {
  const { history, location, children } = props;
  const { d4ur } = useServices();
  const { ui } = useStores();
  const [collapsed, setCollapsed] = useState(window.innerWidth < 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // The event switcher is a custom dropdown rather than a native <select>:
  // macOS renders select popups aligned so the *selected* option sits over the
  // control, which makes the menu open upward once the second event is chosen.
  // Owning the markup keeps it opening downward every time, and lets the menu
  // show full organisation names while the trigger stays truncated.
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef<HTMLDivElement>(null);

  // Brief centre-screen confirmation after an event switch. Switching is
  // instant and the sidebar looks much the same afterwards, so without this
  // there is no feedback that anything changed.
  const [switchConfirm, setSwitchConfirm] = useState<{ label: string; at: number } | null>(null);

  useEffect(() => {
    if (!switchConfirm) return undefined;
    const timer = setTimeout(() => setSwitchConfirm(null), 2200);
    return () => clearTimeout(timer);
  }, [switchConfirm]);

  useEffect(() => {
    if (!switcherOpen) return undefined;

    const onDocumentMouseDown = (event: MouseEvent) => {
      if (switcherRef.current && !switcherRef.current.contains(event.target as Node)) {
        setSwitcherOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSwitcherOpen(false);
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [switcherOpen]);

  useEffect(() => {
    if (ui.events.length === 0 && !ui.isGettingEvents) {
      ui.setIsGettingEvents(true);
      d4ur.getEvents().then((ret: any) => {
        // Only trust the body on a 2xx; on an error status `result` is the
        // API's error object, not a list of events.
        const ok = ret?.response?.ok ?? false;
        ui.setEvents(ok && Array.isArray(ret?.result) ? ret.result : []);
        ui.setIsGettingEvents(false);
      }).catch(() => {
        ui.setIsGettingEvents(false);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const BLOCKED_STATUSES = ['cancelled', 'ineligible'];

  // Events the dealer may switch between: real events only (event requests come
  // back with a null event_id) and nothing cancelled or ineligible. Sorted by
  // start date so the switcher lists them chronologically.
  const getSwitchableEvents = () => {
    return (ui.events || [])
      .filter((event: any) => (
        !!event?.event_id &&
        !BLOCKED_STATUSES.includes((event?.status || '').toString().toLowerCase())
      ))
      .slice()
      .sort((a: any, b: any) => moment(a.start_date).valueOf() - moment(b.start_date).valueOf());
  };

  // Prefer the event running today, else the next one upcoming, else the latest.
  const pickDefaultEvent = (events: any[]) => {
    const today = moment().startOf('day');
    return (
      events.find(isHappeningToday) ||
      events.find((event: any) => (
        event?.start_date && moment(event.start_date).startOf('day').isAfter(today)
      )) ||
      events[events.length - 1]
    );
  };

  // Keep selectedEventID pointing at a real, switchable event. Also accepts an
  // `?event_id=` hand-off from the event portal: the two portals sit on different
  // subdomains, so they cannot share this store's localStorage.
  useEffect(() => {
    const switchable = getSwitchableEvents();
    if (switchable.length === 0) return;

    const isSwitchable = (id: any) => (
      switchable.some((event: any) => Number(event.event_id) === Number(id))
    );

    const fromUrl = new URLSearchParams(location.search).get('event_id');
    if (fromUrl && isSwitchable(fromUrl)) {
      ui.setSelectedEventID(Number(fromUrl));
      return;
    }

    if (!isSwitchable(ui.selectedEventID)) {
      const fallback = pickDefaultEvent(switchable);
      if (fallback) ui.setSelectedEventID(Number(fallback.event_id));
    }
  }, [ui.events]); // eslint-disable-line react-hooks/exhaustive-deps

  const POST_EVENT_STATUSES = ['event ending', 'event ended'];

  // Whether the Dealer Survey nav item should be shown. Defined in terms of
  // getEventThatEnded so visibility and the click handler always agree -- both
  // follow the event switcher rather than scanning every event.
  const hasEventEnded = () => !!getEventThatEnded();

  // A dealer may hold more than one event. `ui.selectedEventID` records which one
  // the portal is currently acting on, and every event-scoped nav action below
  // resolves through it, so the whole sidebar follows the switcher in the header.
  // With no selection the original "first match wins" behaviour is preserved.
  const getSelectedEvent = () => {
    if (!ui.selectedEventID) return undefined;
    return (ui.events || []).find(
      (event: any) => Number(event?.event_id) === Number(ui.selectedEventID)
    );
  };

  const isHappeningToday = (event: any) => {
    if (!event?.start_date || !event?.end_date) return false;
    const today = moment().startOf('day');
    const eventStartDate = moment(event.start_date).startOf('day');
    const eventEndDate = moment(event.end_date).startOf('day');
    return today.isSameOrAfter(eventStartDate) && today.isSameOrBefore(eventEndDate);
  };

  // Get an event with a post-event status (for dealer survey handler)
  const getEventThatEnded = () => {
    const events = ui.events || [];
    const isEnded = (event: any) =>
      POST_EVENT_STATUSES.includes((event?.status || '').toString().toLowerCase());
    const selected = getSelectedEvent();
    // Honour the switcher: the dealer survey belongs to the event in view.
    if (selected) return isEnded(selected) ? selected : undefined;
    return events.find(isEnded);
  };

  // Check if an event is happening today (between start_date and end_date)
  const getCurrentEvent = () => {
    const selected = getSelectedEvent();
    // When an event is selected, event-day actions apply to that event only --
    // if it isn't running today there is no current event, even if the other one is.
    if (selected) return isHappeningToday(selected) ? selected : undefined;
    return (ui.events || []).find(isHappeningToday);
  };

  // PRE Drive Survey / Check IN / Check OUT: event must be happening today AND not ineligible
  const isEventDayActionAvailable = () => {
    const event = getCurrentEvent();
    if (!event) return false;
    return (event?.status || '').toString().toLowerCase() !== 'cancelled';
  };

  // Use an approved event (not yet ended) OR any currently-happening event.
  // When all approved/active events are over, only Dealer Survey stays clickable.
  const getEventForNavAccess = () => {
    const events = ui.events || [];
    const today = moment().startOf('day');

    // The event portal only serves events that haven't finished yet: once an
    // event ends it reports status 'after', which disables every group 1/2 nav
    // item there and leaves those routes unrendered. Linking a dealer into it
    // lands them on a blank page, so an ended event must not grant nav access
    // here either -- the Dealer Survey has its own path via getEventThatEnded.
    const isOpenForNavAccess = (event: any) => {
      if (!event) return false;
      const status = (event?.status || '').toString().toLowerCase();
      if (BLOCKED_STATUSES.includes(status)) return false;
      const endDate = event?.end_date ? moment(event.end_date).startOf('day') : null;
      return !!endDate && !endDate.isBefore(today);
    };

    // An explicit selection wins, provided the event is still open.
    const selected = getSelectedEvent();
    if (selected) return isOpenForNavAccess(selected) ? selected : null;

    // Prefer an approved event that hasn't ended yet
    const approvedEvent = events.find((event: any) => (
      (event?.status || '').toString().toLowerCase() === 'approved' &&
      isOpenForNavAccess(event)
    ));
    if (approvedEvent) return approvedEvent;
    // Also allow any event that is currently in progress, but only if not cancelled
    const currentEvent = getCurrentEvent();
    if (!currentEvent) return null;
    const currentStatus = (currentEvent?.status || '').toString().toLowerCase();
    const blockedStatuses = ['cancelled', 'ineligible'];
    return blockedStatuses.includes(currentStatus) ? null : currentEvent;
  };

  const isNavAccessAvailable = () => {
    return !!getEventForNavAccess();
  };

  const handlePreSurvey = async () => {
    const event = getCurrentEvent();
    if (!event?.event_id || !event?.pre_reg_hash) return;
    
    try {
      // Get the base URL from the event token API (same URL used for magic links)
      const tokenResult: any = await d4ur.getEventToken(String(event.event_id));
      const { url } = tokenResult.result;
      // Use pre_reg_hash directly: url/pre_reg_hash (e.g., localhost:8081/XDBY9)
      window.location.href = `${url}/${event.pre_reg_hash}`;
    } catch (error) {
      console.error('Error generating pre-survey link:', error);
      alert('Failed to generate pre-survey link. Please try again.');
    }
  };

  const handleCheckIn = async () => {
    const currentEvent = getCurrentEvent();
    if (!currentEvent?.event_id) return;
    
    try {
      const tokenResult: any = await d4ur.getEventToken(String(currentEvent.event_id));
      const { token, url } = tokenResult.result;
      // Try query parameter approach - event portal needs to handle redirect to #/checkin
      window.location.href = `${url}/auto_login/${token}?redirect=checkin`;
    } catch (error) {
      console.error('Error generating check-in link:', error);
      alert('Failed to generate check-in link. Please try again.');
    }
  };

  const handleCheckOut = async () => {
    const currentEvent = getCurrentEvent();
    if (!currentEvent?.event_id) return;
    
    try {
      const tokenResult: any = await d4ur.getEventToken(String(currentEvent.event_id));
      const { token, url } = tokenResult.result;
      // Try query parameter approach - event portal needs to handle redirect to #/checkout
      window.location.href = `${url}/auto_login/${token}?redirect=checkout`;
    } catch (error) {
      console.error('Error generating check-out link:', error);
      alert('Failed to generate check-out link. Please try again.');
    }
  };

  const handleDealerSurvey = async () => {
    const event = getEventThatEnded();
    if (!event?.event_id || !event?.pre_reg_hash) return;
    
    try {
      // Get the base URL from the event token API (same URL used for magic links)
      const tokenResult: any = await d4ur.getEventToken(String(event.event_id));
      const { url } = tokenResult.result;
      // Use pre_reg_hash with /dealer-survey: url/pre_reg_hash/dealer-survey
      window.location.href = `${url}/dealer-survey`;
    } catch (error) {
      console.error('Error generating dealer survey link:', error);
      alert('Failed to generate dealer survey link. Please try again.');
    }
  };

  const handleMarketingMaterials = async () => {
    const event = getEventForNavAccess();
    if (!event?.event_id) return;
    
    try {
      const tokenResult: any = await d4ur.getEventToken(String(event.event_id));
      const { token, url } = tokenResult.result;
      // Magic link to /info
      window.location.href = `${url}/auto_login/${token}?redirect=info`;
    } catch (error) {
      console.error('Error generating marketing materials link:', error);
      alert('Failed to generate marketing materials link. Please try again.');
    }
  };


  const handleSharePortalAccess = async () => {
    const event = getEventForNavAccess();
    if (!event?.event_id) return;
    
    try {
      const tokenResult: any = await d4ur.getEventToken(String(event.event_id));
      const { token, url } = tokenResult.result;
      // Magic link to /info
      window.location.href = `${url}/auto_login/${token}?redirect=share-portal-access`;
    } catch (error) {
      console.error('Error generating share portal access link:', error);
      alert('Failed to generate share portal access link. Please try again.');
    }
  };

  const handleEventDayProcess = async () => {
    const event = getEventForNavAccess();
    if (!event?.event_id) return;

    try {
      const tokenResult: any = await d4ur.getEventToken(String(event.event_id));
      const { token, url } = tokenResult.result;
      window.location.href = `${url}/auto_login/${token}?redirect=day-process`;
    } catch (error) {
      console.error('Error generating event day process link:', error);
      alert('Failed to generate event day process link. Please try again.');
    }
  };

  const handleHelp = async () => {
    const event = getEventForNavAccess();
    if (!event?.event_id) return;
    
    try {
      const tokenResult: any = await d4ur.getEventToken(String(event.event_id));
      const { token, url } = tokenResult.result;
      // Magic link to /help
      window.location.href = `${url}/auto_login/${token}?redirect=help`;
    } catch (error) {
      console.error('Error generating help link:', error);
      alert('Failed to generate help link. Please try again.');
    }
  };

  const handleTraining = async () => {
    const event = getEventForNavAccess();
    if (!event?.event_id) return;
    try {
      const tokenResult: any = await d4ur.getEventToken(String(event.event_id));
      const { token, url } = tokenResult.result;
      window.location.href = `${url}/auto_login/${token}?redirect=training`;
    } catch (error) {
      console.error('Error generating training link:', error);
      alert('Failed to generate training link. Please try again.');
    }
  };

  const handlePlanning = async () => {
    const event = getEventForNavAccess();
    if (!event?.event_id) return;
    try {
      const tokenResult: any = await d4ur.getEventToken(String(event.event_id));
      const { token, url } = tokenResult.result;
      window.location.href = `${url}/auto_login/${token}?redirect=planning`;
    } catch (error) {
      console.error('Error generating planning link:', error);
      alert('Failed to generate planning link. Please try again.');
    }
  };

  const navLinks: NavLink[] = [
    { path: '/events', title: 'Dashboard', icon: <DashboardOutlined /> },
    { 
      path: '/training', 
      title: 'Event Training', 
      icon: <BookOutlined />, 
      disabled: !isNavAccessAvailable(),
      onClick: handleTraining
    },
    { 
      path: '/planning', 
      title: 'Event Planning', 
      icon: <FolderOpenOutlined />, 
      disabled: !isNavAccessAvailable(),
      onClick: handlePlanning
    },
    { 
      path: '/materials', 
      title: 'Marketing Materials', 
      icon: <PictureOutlined />, 
      disabled: !isNavAccessAvailable(),
      onClick: handleMarketingMaterials
    },
    { 
      path: '/share', 
      title: 'Share Portal Access', 
      icon: <ShareAltOutlined />, 
      disabled: !isNavAccessAvailable(),
      onClick: handleSharePortalAccess
    },
    { 
      path: '/tracking', 
      title: 'Kit Tracking & Info', 
      icon: <InboxOutlined />, 
      disabled: !isNavAccessAvailable()
    },
    { 
      path: '/help', 
      title: 'Help', 
      icon: <QuestionCircleOutlined />, 
      disabled: !isNavAccessAvailable(),
      onClick: handleHelp
    },
  ];


  const eventDayLinks: NavLink[] = [
    { 
      path: '/process', 
      title: 'Event Day Stats', 
      icon: <CalendarOutlined />, 
      disabled: !isNavAccessAvailable(),
      onClick: handleEventDayProcess
    },
    { 
      path: '/pre-survey', 
      title: 'PRE Drive Survey', 
      icon: <FileTextOutlined />, 
      disabled: !isEventDayActionAvailable(),
      onClick: handlePreSurvey
    },
    { 
      path: '/check-in', 
      title: 'Check IN', 
      icon: <LoginOutlined />, 
      disabled: !isEventDayActionAvailable(),
      onClick: handleCheckIn
    },
    { 
      path: '/check-out', 
      title: 'Check OUT', 
      icon: <LogoutOutlined />, 
      disabled: !isEventDayActionAvailable(),
      onClick: handleCheckOut
    },
    { 
      path: '/dealer-survey', 
      title: 'Dealer Survey', 
      icon: <FormOutlined />, 
      disabled: !hasEventEnded(),
      onClick: handleDealerSurvey
    },
  ];

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setCollapsed(width < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Roughly what fits on two lines of the trigger once the trailing " - MMM D"
  // is accounted for. The CSS clamp cuts anything longer, and it clips from the
  // end -- which would eat the date, the one thing telling two events apart. So
  // the organisation name is shortened here to guarantee the date survives.
  const MAX_ORG_CHARS = 44;

  const orgAndDate = (event: any) => {
    const charity = (event?.charity_partner || event?.partner_name || '').toString().trim();
    const org = charity
      || (event?.event_name || '').toString().trim()
      || `Event ${event?.event_id}`;
    const date = event?.start_date ? moment(event.start_date).format('MMM D') : '';
    return { org, date };
  };

  // e.g. "Habitat for Humanity - Apr 12".
  const eventLabel = (event: any) => {
    const { org, date } = orgAndDate(event);
    const shown = org.length > MAX_ORG_CHARS
      ? `${org.slice(0, MAX_ORG_CHARS - 1).trimEnd()}…`
      : org;
    return date ? `${shown} - ${date}` : shown;
  };

  // Untruncated, for the trigger's title tooltip and the switch confirmation.
  const eventLabelFull = (event: any) => {
    const { org, date } = orgAndDate(event);
    return date ? `${org} - ${date}` : org;
  };

  const switchableEvents = getSwitchableEvents();

  // Organisation for the event currently in view, shown beside the sidebar's
  // close button. Long names are ellipsised by CSS rather than truncated here,
  // so the full name stays available via the title attribute.
  const selectedOrgName = (() => {
    // `charity_partner` is the dealer API's field; the event portal calls the
    // same value `partner_name`, but that shape never reaches this portal.
    const event: any = getSelectedEvent() || getEventForNavAccess() || getEventThatEnded();
    return (event?.charity_partner || '').toString().trim();
  })();

  const handleLogout = async () => {
    await d4ur.doLogout();
    history.push('/');
  };

  const handleNavClick = (navLink: NavLink) => {
    if (navLink.disabled) return;
    if (navLink.onClick) {
      navLink.onClick();
    } else {
      history.push(navLink.path);
    }
    if (isMobile) {
      setCollapsed(true);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const renderIcon = (icon?: React.ReactNode) => {
    if (!icon) return null;
    return <span className="nav-icon">{icon}</span>;
  };

  const renderMenuToggle = () => {
    if (!collapsed) {
      return <div className="menu-spacer"></div>;
    }
    return (
      <button className="menu-toggle-btn" onClick={() => setCollapsed(false)} aria-label="Expand menu">
        <MenuUnfoldOutlined />
      </button>
    );
  };

  return (
    <div className="sidebar-layout">
      {switchConfirm && (
        <div className="event-switch-toast" role="status" aria-live="polite">
          <span className="event-switch-toast-label">Now viewing</span>
          <span className="event-switch-toast-event">{switchConfirm.label}</span>
        </div>
      )}

      {/* Sidebar */}
      <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-top">
          <button
            type="button"
            className="sidebar-close"
            onClick={() => setCollapsed(true)}
            aria-label="Collapse menu"
          >
            <CloseOutlined />
          </button>
          {selectedOrgName && (
            <span className="sidebar-org" title={selectedOrgName}>
              {selectedOrgName}
            </span>
          )}
        </div>
        
        <nav className="sidebar-nav">
          <div className="nav-divider" />
          {navLinks.map((navLink) => {
            if (navLink.hidden) return null;
            return (
              <React.Fragment key={navLink.path}>
                <button
                  className={`nav-item ${isActive(navLink.path) ? 'active' : ''} ${navLink.disabled ? 'disabled' : ''}`}
                  onClick={() => handleNavClick(navLink)}
                  disabled={navLink.disabled}
                >
                  {renderIcon(navLink.icon)}
                  <span className="nav-text">{navLink.title}</span>
                </button>
                <div className="nav-divider" />
              </React.Fragment>
            );
          })}

          {/* Spacer to push event day items to bottom */}
          <div className="nav-spacer"></div>

          {/* Event Day Section */}
          {eventDayLinks.map((navLink) => {
            if (navLink.hidden) return null;
            return (
              <React.Fragment key={navLink.path}>
                <button
                  className={`nav-item ${isActive(navLink.path) ? 'active' : ''} ${navLink.disabled ? 'disabled' : ''}`}
                  onClick={() => handleNavClick(navLink)}
                  disabled={navLink.disabled}
                >
                  {renderIcon(navLink.icon)}
                  <span className="nav-text">{navLink.title}</span>
                </button>
                <div className="nav-divider" />
              </React.Fragment>
            );
          })}
        </nav>
      </div>

      {/* Overlay for mobile */}
      {!collapsed && isMobile && (
        <div className="sidebar-overlay" onClick={() => setCollapsed(true)} />
      )}

      {/* Content Wrapper: Header + Main Content */}
      <div className="content-wrapper">
        {/* Header */}
        <div className="sidebar-header">
          {renderMenuToggle()}
          
        <div className="header-logo-center">
          <img src={fordLogo} alt="Ford Building Together" className="logo" onClick={() => history.push('/events')} />
        </div>
        
        <div className="header-actions">
          {switchableEvents.length > 1 && (
            <div className="event-switcher" ref={switcherRef}>
              <span className="event-switcher-label">Event</span>
              <div className="event-switcher-control">
                <button
                  type="button"
                  className="event-switcher-trigger"
                  aria-haspopup="listbox"
                  aria-expanded={switcherOpen}
                  title={eventLabelFull(getSelectedEvent())}
                  onClick={() => setSwitcherOpen(open => !open)}
                >
                  <span className="event-switcher-value">
                    {eventLabel(getSelectedEvent())}
                  </span>
                  <span className="event-switcher-caret" aria-hidden="true" />
                </button>

                {switcherOpen && (
                  <ul className="event-switcher-menu" role="listbox">
                    {switchableEvents.map((event: any) => {
                      const isSelected =
                        Number(event.event_id) === Number(ui.selectedEventID);
                      return (
                        <li key={event.event_id} role="none">
                          <button
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            className={`event-switcher-option${isSelected ? ' event-switcher-option-selected' : ''}`}
                            onClick={() => {
                              setSwitcherOpen(false);
                              if (isSelected) return;
                              ui.setSelectedEventID(Number(event.event_id));
                              setSwitchConfirm({
                                label: eventLabelFull(event),
                                at: Date.now(),
                              });
                            }}
                          >
                            {eventLabel(event)}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
          <div className="icon-with-tooltip">
            <button className="logout-button" onClick={handleLogout} aria-label="Log Out">
              Log Out
            </button>
          </div>
          <div className="icon-with-tooltip">
            <button className="profile-button" onClick={() => history.push('/profile')} aria-label="Profile">
              <UserOutlined />
            </button>
            <span className="tooltip">Profile</span>
          </div>
        </div>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {children}
        </div>
      </div>
    </div>
  );
};

export default withRouter(observer(SidebarNavContainer));